import React from 'react';
import {
  Info, Database, Trophy, CloudLightning, Heart,
  DollarSign, Shield, CheckCircle, ChevronLeft,
  BookOpen, AlertTriangle, Users, FileText
} from 'lucide-react';

const BackButton = ({ goBack, label = "Back" }) => (
  <button onClick={goBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-medium">
    <ChevronLeft className="w-5 h-5" />{label}
  </button>
);

const Section = ({ icon: Icon, title, iconColor = "text-brand-600", children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-3">
      <Icon className={`w-6 h-6 ${iconColor}`} />
      {title}
    </h2>
    {children}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-gray-700">
        <CheckCircle className="w-4 h-4 text-brand-500 flex-shrink-0 mt-1" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const HowThisGuideWorksPage = ({ goBack }) => {
  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      {/* Hero */}
      <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-2xl p-8 text-white mb-8">
        <Info className="w-12 h-12 mb-4 opacity-90" />
        <h1 className="text-3xl font-black mb-3">How This Guide Works</h1>
        <p className="text-brand-100 text-lg max-w-2xl">
          The Jamaica School Guide was created to help students, parents, and educators
          understand educational options in Jamaica using clear, factual, and publicly available information.
        </p>
        <div className="mt-6 p-4 bg-white/10 rounded-xl">
          <p className="font-semibold text-lg">Our goal is to inform decisions, not judge schools or students.</p>
        </div>
      </div>

      {/* Where Our Information Comes From */}
      <Section icon={Database} title="Where Our Information Comes From" iconColor="text-blue-600">
        <p className="text-gray-600 mb-4">We use data from:</p>
        <BulletList items={[
          "Publicly released Ministry of Education information",
          "Examination summaries published by recognised bodies",
          "Reputable local sources such as official government releases and national media",
          "Information schools choose to make public"
        ]} />
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-blue-800 text-sm">
            <strong>Important:</strong> Not all schools publish the same data. When information is unavailable, we say so clearly.
          </p>
        </div>
      </Section>

      {/* About School Rankings */}
      <Section icon={Trophy} title="About School Rankings" iconColor="text-amber-600">
        <p className="text-gray-600 mb-4">School rankings on this platform:</p>
        <BulletList items={[
          "Are based on publicly available data",
          "Reflect performance for a specific year",
          "Are not government endorsements",
          "Are not measures of student potential, teacher dedication, or school culture"
        ]} />

        <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-100">
          <p className="text-amber-800 text-sm mb-3">
            <strong>Note:</strong> Small differences in scores are often statistically close and should not be over-interpreted.
          </p>
        </div>

        <p className="text-gray-600 mt-4 mb-2">We encourage families to look beyond rankings and explore:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {['Subject strengths', 'School programmes', 'Student support services', 'Location and access'].map((item, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-3 text-center text-sm font-medium text-gray-700">
              {item}
            </div>
          ))}
        </div>
      </Section>

      {/* Disruptions and Emergencies */}
      <Section icon={CloudLightning} title="Disruptions and Emergencies" iconColor="text-purple-600">
        <p className="text-gray-600 mb-4">
          Jamaica is affected by hurricanes and other disruptions that can impact learning.
        </p>
        <p className="text-gray-600 mb-4">When schools or cohorts are affected:</p>
        <BulletList items={[
          "We clearly flag this information",
          "We provide context alongside performance data",
          "We avoid unfair comparisons"
        ]} />
        <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-100">
          <p className="text-purple-800 text-sm font-medium">
            Academic results during disrupted periods should always be interpreted with care.
          </p>
        </div>
      </Section>

      {/* Special Education & Student Support */}
      <Section icon={Heart} title="Special Education & Student Support" iconColor="text-pink-600">
        <p className="text-gray-600 mb-4">We provide general information only about:</p>
        <BulletList items={[
          "Special education services",
          "Exam accommodations",
          "Support processes"
        ]} />
        <div className="mt-4 p-4 bg-pink-50 rounded-lg border border-pink-100">
          <p className="text-pink-800 text-sm">
            <strong>We do not diagnose conditions or recommend specific schools or treatments.</strong>
          </p>
          <p className="text-pink-700 text-sm mt-2">
            Families are encouraged to contact the Ministry of Education and relevant agencies directly for personalised guidance.
          </p>
        </div>
      </Section>

      {/* Paid Listings & Advertising */}
      <Section icon={DollarSign} title="Paid Listings & Advertising" iconColor="text-brand-600">
        <p className="text-gray-600 mb-4">
          Some service providers (such as tutors, uniform suppliers, and educational services) may appear as paid listings.
        </p>

        <div className="bg-brand-50 rounded-lg p-4 border border-brand-100 mb-4">
          <h3 className="font-bold text-brand-800 mb-2">Important to note:</h3>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-brand-700">
              <span className="text-red-500 font-bold">X</span>
              Paid listings do not influence rankings
            </li>
            <li className="flex items-center gap-2 text-brand-700">
              <span className="text-red-500 font-bold">X</span>
              Paid listings do not affect academic data
            </li>
            <li className="flex items-center gap-2 text-brand-700">
              <CheckCircle className="w-4 h-4 text-brand-600" />
              Paid listings are clearly identified
            </li>
          </ul>
        </div>

        <p className="text-gray-700 font-medium">
          Academic information and advertising are kept strictly separate.
        </p>
      </Section>

      {/* Child Safety */}
      <Section icon={Shield} title="Child Safety" iconColor="text-red-600">
        <p className="text-gray-600 mb-4">
          If a situation involves a child's safety or wellbeing, official reporting channels are always prioritised and clearly provided.
        </p>
        <div className="p-4 bg-red-50 rounded-lg border border-red-100">
          <p className="text-red-800 font-medium">
            This platform is not a substitute for emergency or protective services.
          </p>
        </div>
      </Section>

      {/* Our Commitment */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
          <BookOpen className="w-7 h-7 text-brand-600" />
          Our Commitment
        </h2>
        <p className="text-gray-600 mb-4">We are committed to:</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Accuracy', icon: CheckCircle },
            { label: 'Fairness', icon: Users },
            { label: 'Transparency', icon: FileText },
            { label: 'Respect', icon: Heart }
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-lg p-4 text-center shadow-sm">
              <item.icon className="w-6 h-6 text-brand-600 mx-auto mb-2" />
              <span className="font-semibold text-gray-800">{item.label}</span>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <p className="text-gray-700 font-medium mb-2">
            Education is complex. No single dataset tells the whole story.
          </p>
          <p className="text-gray-600">
            We encourage families to use this guide as <strong>one tool among many</strong> when making educational decisions.
          </p>
        </div>
      </div>

      {/* Data Sources Note */}
      <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
        <p className="text-sm text-gray-500 text-center">
          Last updated: January 2026. Data sources include MOEY, CXC, and verified public records.
        </p>
      </div>
    </div>
  );
};

export default HowThisGuideWorksPage;
